import jsPDF from 'jspdf';

export interface PDFGeneratorConfig {
  userName: string;
  imageBase64: string;
  title: string;
  fileName: string;
  orientation?: 'portrait' | 'landscape';
}

export abstract class BasePDFGenerator {
  protected doc: jsPDF;
  protected config: PDFGeneratorConfig;

  constructor(config: PDFGeneratorConfig) {
    this.doc = new jsPDF({ orientation: config.orientation || 'portrait' });
    this.config = config;
  }

  public async generate(): Promise<void> {
    await this.addTitleSection();
    this.addSummarySection();
    this.addContentSection();
    this.saveDocument();
  }

  protected async addTitleSection(selectedRequest?: any): Promise<void> {
    const { userName, imageBase64, title, orientation } = this.config;
    const imgX = 10,
      imgY = 5;
    let imgHeight = 15;

    const { width: naturalWidth, height: naturalHeight } =
      await this.getImageDimensionsFromBase64(imageBase64);
    const aspectRatio = naturalWidth / naturalHeight;
    let imgWidth = imgHeight * aspectRatio;
    const maxWidth = 60;
    if (imgWidth > maxWidth) {
      imgWidth = maxWidth;
      imgHeight = imgWidth / aspectRatio;
    }

    this.doc.addImage(
      imageBase64 as any,
      'PNG',
      imgX,
      imgY,
      imgWidth,
      imgHeight,
    );

    const pageWidth = orientation === 'landscape' ? 297 : 210;
    this.drawText(title, pageWidth / 2, 15, {
      size: 16,
      align: 'center',
      bold: true,
    });

    const rightX = orientation === 'landscape' ? 230 : 150;
    this.drawText(`Fecha: ${new Date().toLocaleString()}`, rightX, 10, {
      size: 10,
    });
    const userToShow = selectedRequest
      ? selectedRequest.showExecutiveNameAsUser
        ? selectedRequest.executiveName
        : userName
      : userName;
    this.drawText(`Usuario: ${userToShow}`, rightX, 15, { size: 10 });
  }

  protected getImageDimensionsFromBase64(
    base64: string,
  ): Promise<{ width: number; height: number }> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.src = base64;
    });
  }

  protected drawText(
    text: string,
    x: number,
    y: number,
    options?: {
      bold?: boolean;
      size?: number;
      align?: 'left' | 'center' | 'right';
    },
  ): void {
    if (options?.size) this.doc.setFontSize(options.size);
    this.doc.setFont('helvetica', options?.bold ? 'bold' : 'normal');
    this.doc.text(
      text,
      x,
      y,
      options?.align ? { align: options.align } : undefined,
    );
  }

  protected drawKeyValue(
    key: string,
    value: string,
    x: number,
    y: number,
    valueOffset: number = 35,
  ): void {
    this.drawText(key, x, y, { bold: true, size: 10 });
    this.drawText(`: ${value}`, x + valueOffset, y, { size: 10 });
  }

  protected truncate(str: string, max: number): string {
    return str.length > max ? str.substring(0, max) + '...' : str;
  }

  protected saveDocument(): void {
    this.doc.save(this.config.fileName);
  }

  // Abstract methods that must be implemented by subclasses
  protected abstract addSummarySection(): void;
  protected abstract addContentSection(): void;
}
